import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Package, MapPin, Heart, LogOut, Plus, Trash2, ChevronDown, ChevronUp, Clock, Pencil, Save, Loader2, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';

const API = `${window.location.origin}/api`;

function getHeaders() {
  const token = localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token}` };
}

const STATUS_COLORS = {
  'Pending Payment': 'text-yellow-400',
  'Paid': 'text-blue-400',
  'Preparing': 'text-purple-400',
  'Shipped': 'text-cyan-400',
  'Completed': 'text-green-400',
  'Rejected': 'text-red-400',
};

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, logout, refreshUser } = useAuth();
  const { addToCart } = useCart();
  const [tab, setTab] = useState('profile');
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addrForm, setAddrForm] = useState({ label: 'Home', house: '', barangay: '', city: '', province: '', zip: '' });
  const [profileForm, setProfileForm] = useState({ name: '', phone: '' });
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate('/login');
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const headers = getHeaders();
    Promise.all([
      axios.get(`${API}/customer/orders`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/customer/addresses`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/customer/wishlist`, { headers }).catch(() => ({ data: [] })),
    ]).then(([o, a, w]) => {
      setOrders(o.data);
      setAddresses(a.data);
      setWishlist(w.data);
    });
  }, [isAuthenticated]);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || '', phone: user.phone || '' });
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSaveProfile = async () => {
    if (!profileForm.name.trim()) { toast.error('Name is required'); return; }
    setSavingProfile(true);
    try {
      await axios.put(`${API}/customer/profile`, profileForm, { headers: getHeaders() });
      await refreshUser();
      toast.success('Profile updated');
      setEditingProfile(false);
    } catch { toast.error('Failed to save'); }
    setSavingProfile(false);
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(`${API}/upload/product-image`, fd, { headers: { Authorization: `Bearer ${token}` } });
      const picUrl = `${API}/files/${res.data.storage_path}`;
      await axios.put(`${API}/customer/profile`, { picture: picUrl }, { headers: { Authorization: `Bearer ${token}` } });
      await refreshUser();
      toast.success('Photo updated');
    } catch { toast.error('Upload failed'); }
    setUploadingPhoto(false);
  };

  const handleRemovePhoto = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API}/customer/profile`, { picture: '' }, { headers: { Authorization: `Bearer ${token}` } });
      await refreshUser();
      toast.success('Photo removed');
    } catch { toast.error('Failed'); }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    if (!addrForm.house || !addrForm.barangay || !addrForm.city || !addrForm.province) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const res = await axios.post(`${API}/customer/addresses`, addrForm, { headers: getHeaders() });
      setAddresses(prev => [...prev, res.data]);
      setAddrForm({ label: 'Home', house: '', barangay: '', city: '', province: '', zip: '' });
      setShowAddressForm(false);
      toast.success('Address saved');
    } catch { toast.error('Failed to save address'); }
  };

  const handleDeleteAddress = async (id) => {
    try {
      await axios.delete(`${API}/customer/addresses/${id}`, { headers: getHeaders() });
      setAddresses(prev => prev.filter(a => a.id !== id));
      toast.success('Address removed');
    } catch { toast.error('Failed to remove'); }
  };

  const handleRemoveWishlist = async (productId) => {
    try {
      await axios.delete(`${API}/customer/wishlist/${productId}`, { headers: getHeaders() });
      setWishlist(prev => prev.filter(p => p.id !== productId));
      toast.success('Removed from wishlist');
    } catch { toast.error('Failed to remove'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-white/40">Loading...</div></div>;
  if (!user) return null;

  const tabs = [
    { key: 'profile', label: 'Profile', icon: User },
    { key: 'orders', label: 'Orders', icon: Package, count: orders.length },
    { key: 'addresses', label: 'Addresses', icon: MapPin, count: addresses.length },
    { key: 'wishlist', label: 'Wishlist', icon: Heart, count: wishlist.length },
  ];

  return (
    <div data-testid="account-page" className="min-h-screen">
      <SEOHead title="My Account" noindex />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Profile Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-12 h-12 rounded-full" />
            ) : (
              <div className="w-12 h-12 bg-[#D4AF37] flex items-center justify-center">
                <User className="w-6 h-6 text-black" />
              </div>
            )}
            <div>
              <h1 className="text-xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{user.name}</h1>
              <p className="text-white/40 text-sm font-sans">{user.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-white/40 hover:text-white text-sm transition-colors" data-testid="account-logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="account-tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`account-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-sans transition-colors whitespace-nowrap ${
                tab === t.key ? 'bg-[#D4AF37] text-black font-medium' : 'border border-white/20 text-white/60 hover:border-white/40'
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
              {t.count > 0 && <span className="text-xs opacity-70">({t.count})</span>}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {tab === 'profile' && (
          <div data-testid="profile-section" className="bg-[#141414] border border-white/5 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Personal Information</h2>
              {!editingProfile ? (
                <button onClick={() => setEditingProfile(true)} className="flex items-center gap-1.5 text-[#D4AF37] text-xs uppercase tracking-wider hover:text-[#F3E5AB] transition-colors" data-testid="edit-profile-btn">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
              ) : (
                <button onClick={handleSaveProfile} disabled={savingProfile} className="flex items-center gap-1.5 bg-[#D4AF37] text-black px-3 py-1.5 text-xs uppercase tracking-wider font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50" data-testid="save-profile-btn">
                  <Save className="w-3.5 h-3.5" /> {savingProfile ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>

            <div className="flex items-start gap-5 mb-6">
              <div className="relative flex-shrink-0">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-20 h-20 rounded-full" />
                ) : (
                  <div className="w-20 h-20 bg-[#D4AF37] flex items-center justify-center rounded-full">
                    <User className="w-10 h-10 text-black" />
                  </div>
                )}
                {user.auth_provider !== 'google' && (
                  <div className="absolute -bottom-1 -right-1 flex gap-1">
                    <label className="w-6 h-6 bg-[#D4AF37] flex items-center justify-center cursor-pointer hover:bg-[#F3E5AB] transition-colors rounded-full" data-testid="upload-photo-btn">
                      {uploadingPhoto ? <Loader2 className="w-3 h-3 text-black animate-spin" /> : <Pencil className="w-3 h-3 text-black" />}
                      <input type="file" accept="image/*" className="hidden" onChange={e => handlePhotoUpload(e.target.files?.[0])} />
                    </label>
                    {user.picture && (
                      <button onClick={handleRemovePhoto} className="w-6 h-6 bg-red-500/80 flex items-center justify-center hover:bg-red-500 transition-colors rounded-full" data-testid="remove-photo-btn">
                        <Trash2 className="w-3 h-3 text-white" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-4 pt-1">
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider font-sans mb-1">Full Name</p>
                  {editingProfile ? (
                    <input type="text" value={profileForm.name} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                      className="w-full bg-transparent border-b border-white/20 text-white py-1 text-sm focus:border-[#D4AF37] focus:outline-none" data-testid="profile-name-input" />
                  ) : (
                    <p className="text-white text-sm font-sans">{user.name || 'Not set'}</p>
                  )}
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider font-sans mb-1">Email</p>
                  <p className="text-white text-sm font-sans">{user.email}</p>
                  {user.auth_provider === 'google' && <span className="text-[10px] text-white/30 font-sans uppercase">Connected via Google</span>}
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider font-sans mb-1">Phone</p>
                  {editingProfile ? (
                    <input type="tel" value={profileForm.phone} onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                      placeholder="09123456789"
                      className="w-full bg-transparent border-b border-white/20 text-white py-1 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30" data-testid="profile-phone-input" />
                  ) : (
                    <p className="text-white text-sm font-sans">{user.phone || <span className="text-white/30 italic">Not set</span>}</p>
                  )}
                </div>
              </div>
            </div>

            {editingProfile && (
              <button onClick={() => { setEditingProfile(false); setProfileForm({ name: user.name || '', phone: user.phone || '' }); }}
                className="text-white/40 text-xs hover:text-white transition-colors">Cancel</button>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {tab === 'orders' && (
          <div data-testid="orders-section">
            {orders.length === 0 ? (
              <div className="text-center py-16 border border-white/5 bg-[#141414]">
                <Package className="w-10 h-10 text-white/15 mx-auto mb-3" />
                <p className="text-white/30 text-sm font-sans mb-4">No orders yet</p>
                <Link to="/shop" className="text-[#D4AF37] text-sm uppercase tracking-wider hover:underline">Start Shopping</Link>
              </div>
            ) : (
              <div className="space-y-2">
                {orders.map(order => (
                  <div key={order.id} className="bg-[#141414] border border-white/5">
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                      data-testid={`order-item-${order.id}`}
                    >
                      <div>
                        <p className="text-white text-sm font-mono">{order.order_number}</p>
                        <p className="text-white/30 text-xs font-sans">{new Date(order.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-sans ${STATUS_COLORS[order.status] || 'text-white'}`}>{order.status}</span>
                        <span className="text-[#D4AF37] text-sm font-sans">₱{order.total?.toLocaleString()}</span>
                        {expandedOrder === order.id ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                      </div>
                    </div>
                    {expandedOrder === order.id && (
                      <div className="border-t border-white/5 p-4 space-y-2">
                        {order.items?.map((item, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-white/70">{item.product_name} x{item.quantity} {item.variant ? `(${item.variant.size}, ${item.variant.type})` : ''}</span>
                            <span className="text-white/50">₱{(item.price * item.quantity).toLocaleString()}</span>
                          </div>
                        ))}
                        {order.tracking_number && (
                          <p className="text-white/40 text-xs mt-2"><Clock className="w-3 h-3 inline mr-1" />Tracking: {order.tracking_number}</p>
                        )}
                        {/* Reorder — replays this order's line items into the
                            current cart. Each call to addToCart merges by
                            (product_id + variant) so repeat items just
                            increment the quantity. */}
                        <div className="pt-3 mt-3 border-t border-white/5 flex flex-wrap items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={async () => {
                              if (!order.items?.length) return;
                              try {
                                // Hydrate full product records so the cart has
                                // accurate prices/images even if the order
                                // snapshot is stale.
                                const resp = await axios.get(`${API}/products`);
                                const byId = new Map((resp.data || []).map(p => [p.id, p]));
                                let added = 0;
                                let skipped = 0;
                                for (const it of order.items) {
                                  const prod = byId.get(it.product_id);
                                  if (!prod) { skipped += 1; continue; }
                                  const variant = it.variant && prod.variants
                                    ? prod.variants.find(v => v.size === it.variant.size && v.type === it.variant.type)
                                    : null;
                                  addToCart(prod, it.quantity || 1, variant);
                                  added += 1;
                                }
                                if (added > 0) {
                                  toast.success(`Reordered ${added} item${added === 1 ? '' : 's'}${skipped ? ` · ${skipped} unavailable` : ''}`);
                                  navigate('/cart');
                                } else {
                                  toast.error('None of these items are available anymore.');
                                }
                              } catch {
                                toast.error('Could not reorder right now.');
                              }
                            }}
                            data-testid={`reorder-btn-${order.id}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.18em] font-sans hover:bg-[#F3E5AB] transition-colors"
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> Reorder
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Addresses Tab */}
        {tab === 'addresses' && (
          <div data-testid="addresses-section">
            <div className="space-y-3 mb-4">
              {addresses.map(addr => (
                <div key={addr.id} className="bg-[#141414] border border-white/5 p-4 flex justify-between items-start">
                  <div>
                    <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-1">{addr.label}</p>
                    <p className="text-white text-sm">{addr.house}, {addr.barangay}</p>
                    <p className="text-white/50 text-sm">{addr.city}, {addr.province} {addr.zip}</p>
                  </div>
                  <button onClick={() => handleDeleteAddress(addr.id)} className="text-white/30 hover:text-red-400 transition-colors" data-testid={`delete-addr-${addr.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {showAddressForm ? (
              <form onSubmit={handleAddAddress} className="bg-[#141414] border border-white/5 p-4 space-y-3" data-testid="address-form">
                <select value={addrForm.label} onChange={e => setAddrForm({ ...addrForm, label: e.target.value })} className="bg-[#0A0A0A] border border-white/20 text-white text-sm px-3 py-2 w-full">
                  <option value="Home">Home</option>
                  <option value="Office">Office</option>
                  <option value="Other">Other</option>
                </select>
                <input type="text" placeholder="House No. / Street / Subdivision" value={addrForm.house} onChange={e => setAddrForm({ ...addrForm, house: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30" required />
                <input type="text" placeholder="Barangay" value={addrForm.barangay} onChange={e => setAddrForm({ ...addrForm, barangay: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30" required />
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="City / Municipality" value={addrForm.city} onChange={e => setAddrForm({ ...addrForm, city: e.target.value })}
                    className="bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30" required />
                  <input type="text" placeholder="ZIP (optional)" value={addrForm.zip} onChange={e => setAddrForm({ ...addrForm, zip: e.target.value })}
                    className="bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30" />
                </div>
                <input type="text" placeholder="Province" value={addrForm.province} onChange={e => setAddrForm({ ...addrForm, province: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30" required />
                <div className="flex gap-2">
                  <button type="submit" className="flex-1 bg-[#D4AF37] text-black py-2 text-sm uppercase tracking-wider font-medium hover:bg-[#F3E5AB] transition-colors" data-testid="save-address-btn">Save</button>
                  <button type="button" onClick={() => setShowAddressForm(false)} className="px-4 border border-white/20 text-white/60 text-sm hover:border-white/40 transition-colors">Cancel</button>
                </div>
              </form>
            ) : (
              <button onClick={() => setShowAddressForm(true)} className="w-full border border-dashed border-white/20 text-white/40 hover:text-white py-3 text-sm flex items-center justify-center gap-2 transition-colors" data-testid="add-address-btn">
                <Plus className="w-4 h-4" /> Add New Address
              </button>
            )}
          </div>
        )}

        {/* Wishlist Tab */}
        {tab === 'wishlist' && (
          <div data-testid="wishlist-section">
            {wishlist.length === 0 ? (
              <div className="text-center py-16 border border-white/5 bg-[#141414]">
                <Heart className="w-10 h-10 text-white/15 mx-auto mb-3" />
                <p className="text-white/30 text-sm font-sans mb-4">No saved fragrances yet</p>
                <Link to="/shop" className="text-[#D4AF37] text-sm uppercase tracking-wider hover:underline">Browse Collection</Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {wishlist.map(product => (
                  <div key={product.id} className="group relative">
                    <Link to={`/product/${product.id}`} className="block">
                      <div className="bg-[#141414] border border-white/5 aspect-[3/4] overflow-hidden mb-2">
                        <img src={product.image_url || ''} alt={product.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <p className="text-white text-sm group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{product.name}</p>
                      <p className="text-[#D4AF37] text-sm font-sans">₱{product.base_price?.toLocaleString()}</p>
                    </Link>
                    <button
                      onClick={() => handleRemoveWishlist(product.id)}
                      className="absolute top-2 right-2 bg-black/60 p-1.5 text-white/50 hover:text-red-400 transition-colors"
                      data-testid={`remove-wishlist-${product.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
